import { AlphaStitchingType, IRoom, MediaType, PlayOption, PublishedUser, RemoteTrackEvent, RemoteTrackEventTypes, RoomEvent, TrackEvent } from '../enums';
import { VideoImageSourceNode } from '../manager/video-node/source';
import { RemoteTrack } from './remote-track';
import { IOS_MAIN_VERSION, IS_ANDROID } from '../utils/environment';
import { Video2dContext } from '../manager/video-context';
import { VideoDestinationNode } from '../manager/video-node/destination';
import { delay, isBoolean, isNumber } from '../utils/utils';
import { isRequestVideoFrameCallbackSupported } from '../common/rtc-detection';
import { YUVSourceNode } from '../manager/video-node/yuv';
import innerEmitter from '../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../inner-emitter/inner-type';
import { AlphaNode } from '../manager/video-node/alpha';
import { PlayerEvent } from '../common/event';

function findClosestFPS(fps: number) {
  const fpsList = [15, 30, 45, 60];
  return fpsList.reduce((prev, curr) => (Math.abs(curr - fps) < Math.abs(prev - fps) ? curr : prev));
}
/**
 * 远端视频的拉取和播放
 */
export class RemoteVideoTrack extends RemoteTrack<'video'> {
  mediaType = MediaType.BIG_VIDEO;
  source?: VideoImageSourceNode | YUVSourceNode;
  shouldRenderAlpha = false;
  private alphaNode?: AlphaNode;
  shouldBeDraggable = true;
  stat: RemoteVideoStat = {
    bytesReceived: 0,
    packetsReceived: 0,
    packetsLost: 0,
    framesReceived: 0,
    framesDecoded: 0,
    frameWidth: 0,
    frameHeight: 0,
    end2EndDelay: 0,
    jitterBufferDelay: 0,
    keyFramesDecoded: 0
  };
  // 用于检测 10s 内关键帧数量
  private _keyFrameCountLogged = false;
  private _keyFrameStartTimestamp = 0;
  private _keyFrameStartCount = 0;
  private _keyFrameIntervals: number[] = [];
  private _prevKeyFrameTimestamp = 0;
  constructor(room: IRoom, user: PublishedUser, mediaType = MediaType.BIG_VIDEO) {
    super(room, user, mediaType);
    this.manager = room.videoManager;
    this.on(TrackEvent.FIRST_VIDEO_FRAME, (data: any) => {
      this.room.emit(RoomEvent.FIRST_VIDEO_FRAME, data);
    });
  }
  isAlphaSei(sei: any) {
    if (this.userId !== sei.userId || sei.seiPayloadType !== 50) return false;
    const uint8Array = new Uint8Array(sei.data);
    if (uint8Array.length % 3 !== 0 || uint8Array[0] !== 0x00000000 || uint8Array[1] !== 0x00000001) return false;
    return uint8Array;
  }
  play(container: HTMLElement | HTMLElement[] | null, options?: PlayOption) {
    if (options?.canvasRender && !this.source) {
      this.useCanvasPlayer();
    }
    return super.play(container, options).then(() => {
      this.player.calculateStat();
      innerEmitter.emit(INNER_EVENT.REMOTE_VIDEO_PLAY_START, { track: this, player: this.player });
    });
  }
  updateAlphaRenderInfo(sei: any) {
    const seiData = this.isAlphaSei(sei);
    if (!seiData) return;
    if (!this.alphaNode) {
      this.shouldRenderAlpha = true;
      this.player.shouldRenderAlpha = true;
      this.useCanvasPlayer(seiData[2]);
    } else {
      const alphaStitchingType = seiData[2];
      if (this.alphaNode.baseRatio && this.alphaNode.initStat.alphaStitchingType === alphaStitchingType) return;
      this.alphaNode.initStat = { alphaStitchingType };
      const videoElement = this.player.getElement();
      if (videoElement) {
        const newRatio = videoElement.videoWidth / videoElement.videoHeight;
        if (newRatio) {
          this.alphaNode.baseRatio = newRatio * (alphaStitchingType === AlphaStitchingType.horizontal ? 0.5 : 2);
          this.alphaNode.onRatioReset();
        }
      }
      if (this.player.canvas) this.player.canvas.id = this.generateAlphaCanvasName(alphaStitchingType);
    }
  }
  generateAlphaCanvasName(initAlphaStitchingType: AlphaStitchingType) {
    const alphaName = 'alpha';
    const stitchingTypeName = AlphaStitchingType[initAlphaStitchingType];
    return `${alphaName}_${stitchingTypeName}_${this.userId}`;
  }
  useCanvasPlayer(initAlphaStitchingType?: AlphaStitchingType) {
    this.log.info(`useCanvasPlayer(), has element:${!!this.player.element}`);
    if (!this.player.element) return;
    const ctx2d = new Video2dContext({ frameRate: 15, logger: this.log, name: this.shouldRenderAlpha && initAlphaStitchingType ? this.generateAlphaCanvasName(initAlphaStitchingType) : this.userId });
    ctx2d.create({ alpha: this.shouldRenderAlpha, willReadFrequently: this.shouldRenderAlpha });
    const dest = new VideoDestinationNode(ctx2d, { name: 'remotePlayer', logger: this.log });
    this.source = ctx2d.createVideoPlayerSource(this.player);
    this.player.setCanvas(ctx2d._canvas);
    if (this.shouldRenderAlpha && initAlphaStitchingType) {
      const setContainer = () => {
        if (!this.player.container || !this.alphaNode) return;
        this.alphaNode.container = this.player.container;
        this.alphaNode.renderCanvas();
      };
      const alpha = new AlphaNode(ctx2d, this.log, this.shouldBeDraggable, setContainer, { alphaStitchingType: initAlphaStitchingType });
      this.source.connect(alpha);
      alpha.connect(dest);
      this.alphaNode = alpha;
    } else {
      this.source.connect(dest);
    }
    if (!isRequestVideoFrameCallbackSupported()) {
      this.updateCanvasPlayerFPS = this.updateCanvasPlayerFPS.bind(this, ctx2d);
      this.room.on(RoomEvent.HEARTBEAT_REPORT, this.updateCanvasPlayerFPS, this);
    }
  }
  updateCanvasPlayerFPS(ctx2d: Video2dContext) {
    const frameRate = this.decodeFPS;
    const ClosestFPS = findClosestFPS(frameRate);
    if (!isNumber(frameRate) || frameRate <= 0) {
      this.log.debug(`updateCanvasPlayerFPS() ignore decoder: ${frameRate} `);
      return;
    }
    if (ClosestFPS === ctx2d.frameRate) {
      this.log.debug(`updateCanvasPlayerFPS() ignore ClosestFPS ${ClosestFPS} == ${ctx2d.frameRate}`);
      return;
    }
    this.log.info(`updateCanvasPlayerFPS() decoder: ${frameRate}, closest: ${ClosestFPS}, current: ${ctx2d.frameRate}`);
    ctx2d.frameRate = ClosestFPS;
  }
  get decodeFPS() {
    const { msg_video_status } = this.room.heartbeatReport?.msg_down_stream_info.find(item => item.msg_user_info.str_identifier === this.userId) || {};
    // aux 7, big 2, small 3
    const currentVideoUnit32 = this.mediaType === MediaType.AUX_VIDEO ? 7 : this.isSmall ? 3 : 2;
    if (!msg_video_status || msg_video_status.length === 0) return 0;
    const currentVideo = msg_video_status.find((item: any) => item.uint32_video_stream_type === currentVideoUnit32);
    const fps = currentVideo?.uint32_video_dec_fps || 0;
    return fps;
  }
  stop() {
    this.room.off(RoomEvent.HEARTBEAT_REPORT, this.updateCanvasPlayerFPS, this);
    innerEmitter.emit(INNER_EVENT.REMOTE_VIDEO_PLAY_FINISH, { track: this, player: this.player });
    if (this.alphaNode) this.alphaNode.close();
    return super.stop();
  }
  decodeFrame(frame: RTCEncodedVideoFrame): RTCEncodedVideoFrame | undefined {
    if (!this.manager) return frame;
    for (const decoder of this.manager.decodePipeline) {
      if (!decoder) continue;
      (frame as RTCEncodedVideoFrame | undefined) = decoder({ frame, track: this });
      if (!frame) return;
    }
    return frame;
  }
  get isBig() {
    return this.mediaType === MediaType.BIG_VIDEO;
  }
  get isSmall() {
    return this.mediaType === MediaType.SMALL_VIDEO;
  }
  changeType(isSmall: boolean) {
    this.room.changeType(isSmall, this.user);
  }

  get isRemotePublished() {
    return this.user.muteState.videoAvailable;
  }
  setMirror(mirror?: boolean | string) {
    if (mirror === 'publish' || mirror === 'both') {
      return;
    }
    super.setMirror(mirror);
  }
  setDraggable(draggable: boolean) {
    this.shouldBeDraggable = draggable;
    if (!this.alphaNode) return;
    this.alphaNode.draggable = draggable;
    this.alphaNode.bindDragEvents();
  }
  onDecodeDowngradeStateChanged(event: RemoteTrackEventTypes[RemoteTrackEvent.DECODE_DOWNGRADE_STATE_CHANGED][0]) {
    this.emit(RemoteTrackEvent.DECODE_DOWNGRADE_STATE_CHANGED, event);
  }
  // 检测 16s 内关键帧数量
  updateKeyFramesDecoded(keyFramesDecoded: number) {
    const prevKeyFramesDecoded = this.stat.keyFramesDecoded || 0;
    this.stat.keyFramesDecoded = keyFramesDecoded;

    if (this._keyFrameCountLogged) return;

    const now = Date.now();
    if (!this._keyFrameStartTimestamp) {
      this._keyFrameStartTimestamp = now;
      this._keyFrameStartCount = keyFramesDecoded;
      this._prevKeyFrameTimestamp = now;
      return;
    }

    // 记录每次关键帧间隔
    if (this._prevKeyFrameTimestamp && keyFramesDecoded > prevKeyFramesDecoded) {
      const keyFramesDiff = keyFramesDecoded - prevKeyFramesDecoded;
      const timeDiff = (now - this._prevKeyFrameTimestamp) / 1000;
      const keyFrameInterval = timeDiff / keyFramesDiff;
      this._keyFrameIntervals.push(keyFrameInterval);
    }
    this._prevKeyFrameTimestamp = now;

    const elapsed = now - this._keyFrameStartTimestamp;
    if (elapsed >= 16000) {
      const keyFrameCount = keyFramesDecoded - this._keyFrameStartCount;
      const avgInterval = keyFrameCount > 0 ? elapsed / 1000 / keyFrameCount : 0;
      const logMsg = `${keyFrameCount} keyframes in 16s ${avgInterval} [${this._keyFrameIntervals.map(v => v.toFixed(1)).join(',')}] keyFramesDecoded ${keyFramesDecoded}`;
      const logLevel = avgInterval <= 2.5 ? 'debug' : 'info';
      this.log[logLevel](logMsg);
      this._keyFrameCountLogged = true;
    }
  }
}

export class RemoteAuxiliaryTrack extends RemoteVideoTrack {
  mediaType = MediaType.AUX_VIDEO;
  objectFit: 'contain' | 'cover' | 'fill' = 'contain';
  constructor(room: IRoom, user: PublishedUser) {
    super(room, user, MediaType.AUX_VIDEO);
  }
  get isRemotePublished() {
    return this.user.muteState.hasAuxiliary;
  }
}
